import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { TableView } from '@tiptap/extension-table';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { blockStyle, cellStyle, headingNumbers, normalizeFormatting, tableDisplayGeometry, type LayoutNode } from '@atek/eqms-document-layout';

const blockKeys = ['textAlign', 'lineHeight', 'spacingBefore', 'spacingAfter', 'indent', 'textIndent', 'fontSize', 'color'];
const cellKeys = [...blockKeys, 'backgroundColor', 'padding', 'borderWidth', 'borderStyle', 'borderColor'];
const cssNames: Record<string, string> = { textAlign: 'text-align', lineHeight: 'line-height', spacingBefore: 'margin-top', spacingAfter: 'margin-bottom', indent: 'margin-left', textIndent: 'text-indent', fontSize: 'font-size', color: 'color', backgroundColor: 'background-color', padding: 'padding', borderWidth: 'border-width', borderStyle: 'border-style', borderColor: 'border-color', rowHeight: 'height', tableWidth: 'width' };

/** Tiptap's resize view applies colwidth directly; retain our safe table width too. */
export class AuthoringTableView extends TableView {
  private readonly editorView?: EditorView;
  private frame = 0;
  constructor(...args: ConstructorParameters<typeof TableView>) {
    super(...args); this.editorView = args[2]; this.applyWidth(args[0]);
    // Node views are created before React attaches the paper container.
    this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.dom.isConnected) this.applyWidth(this.node); });
  }
  update(node: ProseMirrorNode) { const updated = super.update(node); if (updated) this.applyWidth(node); return updated; }
  destroy() { if (this.frame) cancelAnimationFrame(this.frame); }
  private applyWidth(node: ProseMirrorNode) {
    if (this.editorView?.state.doc.attrs.layoutVersion === 2) {
      const paper = this.editorView.dom.closest('[data-authoring-paper]');
      const available = paper instanceof HTMLElement ? paper.clientWidth - Number.parseFloat(getComputedStyle(paper).paddingLeft) - Number.parseFloat(getComputedStyle(paper).paddingRight) : 720;
      const geometry = tableDisplayGeometry(node.toJSON() as LayoutNode, available);
      this.table.style.width = `${geometry.width}px`; this.table.style.minWidth = '';
      [...this.colgroup.children].forEach((column, index) => {
        if (column instanceof HTMLElement) { column.style.removeProperty('min-width'); column.style.width = `${geometry.colwidth[index]}px`; }
      });
    } else if (typeof node.attrs.tableWidth === 'number') this.table.style.width = `${node.attrs.tableWidth}%`;
  }
}

function attributes(keys: string[], kind: 'block' | 'cell' | 'row' | 'table' | 'number') {
  return Object.fromEntries(keys.map(key => [key, {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const raw = element.getAttribute(`data-${key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`) || (cssNames[key] ? element.style.getPropertyValue(cssNames[key]) : '');
      if (!raw) return null;
      const value = ['numbered', 'hierarchical'].includes(key) ? raw === 'true' : ['textAlign', 'color', 'backgroundColor', 'borderStyle', 'borderColor', 'numberPrefix'].includes(key) ? raw : Number.parseFloat(raw);
      try { return normalizeFormatting({ [key]: value })[key as keyof ReturnType<typeof normalizeFormatting>] ?? null; } catch { return null; }
    },
    renderHTML: (attrs: Record<string, unknown>) => {
      if (attrs[key] == null) return {};
      const value = normalizeFormatting({ [key]: attrs[key] })[key as keyof ReturnType<typeof normalizeFormatting>];
      if (kind === 'cell') return { style: cellStyle({ [key]: value }) };
      if (kind === 'block') return { style: blockStyle({ [key]: value }) };
      if (key === 'rowHeight') return { style: `height:${value}px` };
      if (key === 'tableWidth') return { style: `width:${value}%` };
      if (key === 'hierarchical') return { 'data-hierarchical': String(value), style: `counter-reset:eqms-item ${Number(attrs.numberStart ?? attrs.start ?? 1) - 1}` };
      if (key === 'numberPrefix' && value) return { 'data-number-prefix': String(value), style: `--eqms-prefix:"${value}."` };
      if (key === 'numberStart') return { 'data-number-start': String(value), style: `counter-reset:eqms-item ${Number(value) - 1}` };
      return { [`data-${key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`]: String(value) };
    },
  }]));
}

/** Source attrs are shared with the DMS renderer, never arbitrary CSS. */
export const AuthoringFormatting = Extension.create({
  name: 'authoringFormatting',
  addGlobalAttributes() {
    return [
      { types: ['paragraph', 'heading', 'blockquote', 'orderedList', 'bulletList', 'listItem'], attributes: attributes(blockKeys, 'block') },
      { types: ['tableCell', 'tableHeader'], attributes: attributes(cellKeys, 'cell') },
      { types: ['tableRow'], attributes: attributes(['rowHeight'], 'row') },
      { types: ['table'], attributes: attributes(['tableWidth'], 'table') },
      { types: ['heading'], attributes: attributes(['numbered', 'numberStart', 'numberPrefix'], 'number') },
      { types: ['orderedList'], attributes: attributes(['hierarchical', 'numberStart', 'numberPrefix'], 'number') },
    ];
  },
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('eqms-heading-numbers'),
      props: {
        decorations: state => {
          const json = state.doc.toJSON() as LayoutNode;
          const numbers = headingNumbers(json);
          const labels: string[] = [];
          const walk = (node: LayoutNode) => { if (node.type === 'heading') labels.push(numbers.get(node) || ''); node.content?.forEach(walk); };
          walk(json); let index = 0; const result: Decoration[] = [];
          let sectionHeading = 0; let appendix = false; let revision = false;
          state.doc.descendants((node, pos) => {
            if (state.doc.attrs.layoutVersion !== 2) {
              if (node.type.name === 'procedureSection') { sectionHeading = 0; appendix = false; revision = false; }
              if (node.type.name === 'pageBreak') appendix = true;
              if (node.type.name === 'heading' && node.attrs.level === 1) {
                sectionHeading++;
                if (sectionHeading === 10) { revision = true; result.push(Decoration.node(pos, pos + node.nodeSize, { class: 'procedure-changes' })); }
              }
              if (node.type.name === 'table' && (appendix || revision)) {
                result.push(Decoration.node(pos, pos + node.nodeSize, { class: appendix ? 'procedure-appendix' : 'procedure-revision' })); appendix = false; revision = false;
              }
            }
            if (node.type.name !== 'heading') return;
            const label = labels[index++];
            if (label) result.push(Decoration.widget(pos + 1, () => {
              const span = document.createElement('span'); span.className = 'eqms-heading-number'; span.textContent = label;
              span.contentEditable = 'false'; return span;
            }, { side: -1, key: `heading-${pos}-${label}` }));
          });
          return DecorationSet.create(state.doc, result);
        },
        handleDOMEvents: {
          mousemove: (view, event) => {
            if (!view.editable) return false;
            const cell = (event.target as HTMLElement).closest('td,th');
            if (!(cell instanceof HTMLElement)) return false;
            const rect = cell.getBoundingClientRect();
            cell.style.cursor = Math.abs(event.clientY - rect.bottom) <= 6 ? 'row-resize' : '';
            return false;
          },
          mousedown: (view, event) => {
            if (!view.editable || event.button !== 0) return false;
            const cell = (event.target as HTMLElement).closest('td,th');
            const row = cell?.closest('tr');
            if (!(cell instanceof HTMLElement) || !(row instanceof HTMLElement) || Math.abs(event.clientY - cell.getBoundingClientRect().bottom) > 6) return false;
            const resolved = view.state.doc.resolve(view.posAtDOM(cell, 0));
            let depth = resolved.depth; while (depth > 0 && resolved.node(depth).type.name !== 'tableRow') depth--;
            if (!depth) return false;
            const pos = resolved.before(depth); const start = event.clientY; const height = row.getBoundingClientRect().height;
            const move = (next: MouseEvent) => { row.style.height = `${Math.max(20, Math.min(600, height + next.clientY - start))}px`; };
            const finish = (next: MouseEvent) => {
              document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', finish);
              const node = view.state.doc.nodeAt(pos);
              if (node?.type.name === 'tableRow') view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, rowHeight: Math.round(Math.max(20, Math.min(600, height + next.clientY - start))) }));
            };
            document.addEventListener('mousemove', move); document.addEventListener('mouseup', finish); event.preventDefault(); return true;
          },
        },
      },
    })];
  },
});
